const formatCompressionSize = (bytes) => (bytes >= 1024 * 1024
  ? `${(bytes / (1024 * 1024)).toFixed(2)} MB`
  : `${(bytes / 1024).toFixed(1)} KB`);

const formatDuration = (seconds) => `${Number(seconds || 0).toFixed(1)}s`;

export const createUniqueFileName = (fileName = 'image.jpg') => {
  const safeName = String(fileName).replace(/[^\w.-]/g, '_');
  return `${Date.now()}_${Math.floor(Math.random() * 10000)}_${safeName}`;
};

const logCompressionResult = (scope, file, finalFile, status, reason = '', durationSec = null) => {
  const savedPct = file.size > 0 ? ((1 - (finalFile.size / file.size)) * 100).toFixed(1) : '0.0';
  console.log(
    `[${scope}] | original: ${formatCompressionSize(file.size)} | final: ${formatCompressionSize(finalFile.size)} | saved: ${savedPct}% | duration: ${durationSec !== null ? formatDuration(durationSec) : 'n/a'} | status: ${status}${reason ? ` | reason: ${reason}` : ''} | file: ${file.name}`
  );
};

const logCompressionSkip = (scope, file, reason) => {
  console.log(
    `[${scope}] | original: ${formatCompressionSize(file.size)} | final: ${formatCompressionSize(file.size)} | saved: 0.0% | duration: n/a | status: original kept | reason: ${reason} | file: ${file.name}`
  );
};

export const getProductImageTargetRange = (fileSizeBytes) => {
  if (fileSizeBytes >= 700 * 1024) return [400, 500];
  if (fileSizeBytes >= 300 * 1024) return [200, 300];
  return [200, 250];
};

export const compressProductImage = (file, targetKbRange) => {
  const startedAt = Date.now();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => reject(new Error('Failed to load image'));
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;

        const maxDim = 1600;
        if (width > maxDim || height > maxDim) {
          const ratio = Math.min(maxDim / width, maxDim / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        let minQ = 0.3;
        let maxQ = 0.95;
        let q = 0.75;
        let attempts = 0;

        const finish = (blob) => {
          if (blob.size < file.size) {
            const compressed = new File([blob], createUniqueFileName(`${file.name.replace(/\.[^/.]+$/, '')}.jpg`), {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            logCompressionResult('Product Image', file, compressed, 'compressed', `iterations: ${attempts}`, (Date.now() - startedAt) / 1000);
            resolve(compressed);
          } else {
            logCompressionResult('Product Image', file, file, 'original kept', `already optimized; iterations: ${attempts}`, (Date.now() - startedAt) / 1000);
            resolve(file);
          }
        };

        const iterate = () => {
          canvas.toBlob((blob) => {
            if (!blob) {
              reject(new Error('Failed to compress image'));
              return;
            }
            attempts += 1;
            const kbRes = blob.size / 1024;

            if ((kbRes >= targetKbRange[0] && kbRes <= targetKbRange[1]) || attempts >= 5) {
              finish(blob);
              return;
            }

            if (kbRes > targetKbRange[1]) {
              maxQ = q;
              q = (minQ + maxQ) / 2;
            } else {
              minQ = q;
              q = (minQ + maxQ) / 2;
            }
            iterate();
          }, 'image/jpeg', q);
        };

        iterate();
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
};

export const prepareProductImageFile = async (file) => {
  if (!file?.type?.startsWith('image/')) {
    throw new Error('Please select a valid image file');
  }

  if (file.size > 1 * 1024 * 1024) {
    throw new Error('Image exceeds the 1MB limit');
  }

  if (file.size < 200 * 1024) {
    logCompressionSkip('Product Image', file, 'limit guard: below 200 KB threshold');
    return file;
  }

  const targetRange = getProductImageTargetRange(file.size);
  console.log(`[Product Image] Compressing: ${file.name} (${(file.size / 1024).toFixed(1)} KB)`);
  return compressProductImage(file, targetRange);
};

/** Cropped product photos can start above 1MB. Compress those, then apply the normal size rules. */
export const finalizeProductImageFile = async (file) => {
  if (!file?.type?.startsWith('image/')) {
    throw new Error('Please select a valid image file');
  }
  if (file.size > 1 * 1024 * 1024) {
    return compressProductImage(file, getProductImageTargetRange(file.size));
  }
  return prepareProductImageFile(file);
};
